// api.js - Fully encrypted API system - ALL calls encrypted by default
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { toast } from 'react-toastify';

// Configuration
const BASE_URL = process.env.REACT_APP_BASE_URL
export const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
export const NODE_ENV = process.env.REACT_APP_ENV

// const BASE_URL = `http://4.248.35.15:8080/api`
const SECRET_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'MySecretKey12345678901234567890';

// =====================================================
// CENTRALIZED LOADER MANAGEMENT
// =====================================================
let globalLoaderCount = 0;

const showGlobalLoader = () => {
  globalLoaderCount++;
  if (window.setGlobalLoader) {
    window.setGlobalLoader(true);
  }
};

const hideGlobalLoader = () => {
  globalLoaderCount = Math.max(0, globalLoaderCount - 1);
  if (globalLoaderCount === 0 && window.setGlobalLoader) {
    window.setGlobalLoader(false);
  }
};

// =====================================================
// ENCRYPTION FUNCTIONS
// =====================================================
export const encryptData = (data) => {
  try {
    // console.log("submitting data => ", data)
    const jsonString = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY);
    return encrypted.toString();
  } catch (error) {
    console.error('Encryption failed:', error);
    throw error;
  }
};

export const decryptData = (encryptedString) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedString, SECRET_KEY);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('Decryption failed:', error);
    throw error;
  }
};

// =====================================================
// AXIOS INSTANCE WITH INTERCEPTORS
// =====================================================
const apiClient = axios.create({
  baseURL: BASE_URL,
    timeout: 200000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - GENERIC error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // For 401 errors, only try refresh if we have a refresh token AND a current token
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refresh-token');
      const currentToken = localStorage.getItem('token');

      // Only attempt refresh if:
      // 1. We have a refresh token
      // 2. We had a current token (meaning user was previously authenticated)
      // 3. This isn't already a refresh request
      if (refreshToken && currentToken && !error.config?.url?.includes('/auth/refresh')) {
        try {
          const response = await axios.post(`${BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken
          });

          if (response.data.access_token) {
            localStorage.setItem('token', response.data.access_token);

            // Retry original request
            error.config.headers.Authorization = `Bearer ${response.data.access_token}`;
            return apiClient.request(error.config);
          }
        } catch (refreshError) {
          // Refresh failed - clear tokens but don't redirect
          localStorage.removeItem('token');
          localStorage.removeItem('refresh-token');
          localStorage.removeItem('user-type');
        }
      }
    }

    // Always return the error - let components handle it
    return Promise.reject(error);
  }
);

// =====================================================
// MAIN API FUNCTION - ENCRYPT EVERYTHING BY DEFAULT
// =====================================================
export const apiCall = async (endpoint, method = 'GET', data = null, options = {}, loading = true) => {
  const isPayment = endpoint.includes('/subscription'); // if endpoint includes /subscription, then true else false

  const {
    encrypt = !isPayment, // subscribe to a package is not encrypted, else ALWAYS encrypt by default (isPayment = true then we do not encrypt the data)
    // showLoader = true,
    showLoader = loading,
    showError = true,
    ...axiosOptions
  } = options;

  // Show loader if requested
  if (showLoader) {
    showGlobalLoader();
  }

  try {
    let requestData = data;

    // Encrypt ALL data for ALL methods (including GET with params)
    if (encrypt && data) {
      const encryptedPayload = encryptData(data);
      requestData = { encryptedPayload };
    }

    // For GET requests with params, encrypt the params too
    if (encrypt && method === 'GET' && axiosOptions.params) {
      const encryptedParams = encryptData(axiosOptions.params);
      axiosOptions.params = { encryptedParams };
    }

    if (isPayment) { // if isPayment = true then we add the header x-user-type
      axiosOptions.headers = { ...axiosOptions.headers, 'x-user-type': data?.userRole || "" };
    }

    // Make request with encryption headers for all encrypted calls
    const response = await apiClient({
      url: endpoint,
      method,
      data: requestData,
      headers: encrypt ? { 'X-Fully-Encrypted': 'true' } : {},
      ...axiosOptions
    });

    // Decrypt response if needed
    if (response.data?.encryptedPayload) {
      response.data = decryptData(response.data.encryptedPayload);
    }

    return response;

  } catch (error) {
    // Generic error handling - only show toast if requested
    if (showError) {
      const message = error.response?.data?.message || error.message || 'Something went wrong';
      toast.error(message);
    }
    throw error;
    // return error;
  } finally {
    // Hide loader if it was shown
    if (showLoader) {
      hideGlobalLoader();
    }
  }
};

// =====================================================
// FULLY ENCRYPTED API ENDPOINTS - ALL CALLS ENCRYPTED
// =====================================================
export const API = {
  // Auth endpoints - ALL ENCRYPTED
  auth: {
    login: (credentials) => apiCall('/auth/login', 'POST', credentials),
    adminLogin: (data) => apiCall(`/auth/admin/login`, 'POST', data),
    checkAdminAccess: () => apiCall(`/auth/admin/check-access`, 'GET'),
    loginForPayment: (credentials) => apiCall('/auth/verifyuser', 'POST', credentials),

    logout: () => apiCall('/auth/logout', 'POST', {}),
    resetPassword: (token, password) => apiCall('/auth/reset-password', 'POST', { token, password }),
    // NEW - SINGLE PASSWORD RESET ENDPOINT
    resetPasswordDirect: (data) => apiCall('/auth/reset-password-direct', 'POST', data),
    verifyEmail: (data) => apiCall('/auth/verify-email', 'POST', data, {
      showLoader: true,   // Show loader during API call
      showError: false    // Don't show toast, we handle errors in component
    }),
    resendVerificationEmail: (data) => apiCall('/auth/resend-verification', 'POST', data, {
      showLoader: true,   // Show loader during API call
      showError: false    // Don't show toast, we handle errors in component
    }),
    forgotPassword: (data) => apiCall('/auth/forgot-password', 'POST', data, {
      showLoader: true,   // Show loader during API call
      showError: false    // Don't show toast, we handle errors in component
    }),
    // Password Reset Flow - Token-based
    requestPasswordReset: (data) => apiCall('/auth/request-password-reset', 'POST', data, {
      showLoader: true,
      showError: false
    }),
    validateResetToken: (data) => apiCall('/auth/validate-reset-token', 'POST', data, {
      showLoader: true,
      showError: false
    }),
    resetPasswordWithToken: (data) => apiCall('/auth/reset-password-with-token', 'POST', data, {
      showLoader: true,
      showError: false
    }),
  },


  // Client endpoints - ALL ENCRYPTED
  client: {
    getAll: (params = {}) => apiCall('/clients', 'GET', null, { params }),
    getById: (id) => apiCall(`/clients/${id}`, 'GET'),
    create: (data) => apiCall('/auth/client/register', 'POST', data),
    update: (id, data) => apiCall(`/clients/${id}`, 'PUT', data),
    delete: (id) => apiCall(`/clients/${id}`, 'DELETE'),
    getDocuments: (id) => apiCall(`/clients/${id}/documents`, 'GET'),
    getAllClientTypes: (params = {}) => apiCall('/configuration/getAllClientType', 'GET', null, { params }, false)
  },

  // Advisor endpoints - ALL ENCRYPTED
  advisor: {
    getAll: (params = {}) => apiCall('/advisors', 'GET', null, { params }),
    getById: (id) => apiCall(`/advisors/${id}`, 'GET'),
    create: (data) => apiCall('/auth/register', 'POST', data),
    update: (id, data) => apiCall(`/advisors/${id}`, 'PUT', data),
    delete: (id) => apiCall(`/advisors/${id}`, 'DELETE'),
    getClients: (id) => apiCall(`/advisors/${id}/clients`, 'GET'),
    getADvisorDoc: (id) => apiCall(`/advisorsDocument`, 'GET'),
    getAdvisorTypes: (params = {}) => apiCall('/configuration/getadvisortype', 'GET', null, { params }, false)
  },
  /* jurisdiction: {
    getAll: (params = {}) => apiCall('/jurisdiction', 'GET', null, { params })
  }, */
  pricing: {
    getPricing: () => apiCall(`/subscription/subscription-template`, 'GET'),
    getGlossary: () => apiCall(`/subscription/glossary`, 'GET', null, { encrypt: false }),
    createPaymentIntent: (params = {}) => apiCall(`/subscription/create-payment-intent`, 'POST', { params }),
    createPaymentIntentForTrialUpgrade: (params = {}) => apiCall(`/subscription/create-payment-intent-trial-upgrade`, 'POST', { params }),
    getPaymentStatus: (paymentIntentId) => apiCall(`/subscription/payment-status/${paymentIntentId}`, 'GET')
  },
  contactUs: {
    submit: (data) => apiCall('/configuration/contactus', 'POST', data, { encrypt: false })
  },
  emailValidation: {
    validateEmail: (data) => apiCall('/configuration/validate-email', 'POST', data, {
      showLoader: false,  // Don't show loader while user is typing
      showError: false    // Don't show toast error, we handle it in the component
    })
  },

  // Reference confirmation endpoints (public - no auth required)
  references: {
    // Validate reference token and get details before showing form
    validateToken: (token) => apiCall('/advisor/references/validate', 'GET', null, {
      params: { token },
      encrypt: false,  // Don't encrypt - token is already in JWT format
      showLoader: true,
      showError: false
    }),
    // Confirm or decline reference with optional testimonial
    confirm: (data) => apiCall('/advisor/references/confirm', 'PATCH', data, {
      encrypt: false,  // Don't encrypt - token is in the body
      showLoader: true,
      showError: false
    })
  },

  // Generic method for any endpoint - ENCRYPTED BY DEFAULT
  request: (endpoint, method = 'GET', data = null, options = {}) => {
    return apiCall(endpoint, method, data, options);
  }
};

export default API;